import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getAccessModules, getAccessCategories, getAccessMatrix, saveAccessMatrix, deleteAccessMatrixRow } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Card } from '../components/ui/card';
import { ShieldCheck, Plus, Trash2, Loader2, Save } from 'lucide-react';

export const AccessControlPage = () => {
  const { isSuperAdmin } = useAuth();
  const [modules, setModules] = useState([]);
  const [rows, setRows] = useState([]); // [{category, modules:{key:bool}}]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newCategory, setNewCategory] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, cRes, matRes] = await Promise.all([
        getAccessModules(), getAccessCategories(), getAccessMatrix(),
      ]);
      const mods = mRes.data.modules || [];
      setModules(mods);
      const matrix = matRes.data.matrix || [];
      const cats = cRes.data.categories || [];
      const byCat = {};
      matrix.forEach(row => { byCat[row.category] = row.modules || {}; });
      const merged = cats.map(cat => ({
        category: cat,
        modules: Object.fromEntries(mods.map(m => [m.key, byCat[cat]?.[m.key] ?? true])),
      }));
      setRows(merged);
    } catch (e) {
      toast.error('Failed to load access matrix');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isSuperAdmin) load(); }, [isSuperAdmin, load]);

  if (!isSuperAdmin) return <Navigate to="/" replace />;

  const toggle = (catIdx, key) => {
    setRows(prev => prev.map((r, i) => i === catIdx
      ? { ...r, modules: { ...r.modules, [key]: !r.modules[key] } }
      : r));
  };

  const addCategory = () => {
    const cat = newCategory.trim();
    if (!cat) return;
    if (rows.some(r => r.category.toLowerCase() === cat.toLowerCase())) {
      toast.error('Category already exists'); return;
    }
    setRows(prev => [...prev, { category: cat, modules: Object.fromEntries(modules.map(m => [m.key, true])) }]);
    setNewCategory('');
  };

  const removeCategory = async (cat) => {
    try { await deleteAccessMatrixRow(cat); } catch { /* row may not exist server-side */ }
    setRows(prev => prev.filter(r => r.category !== cat));
    toast.success(`Removed "${cat}"`);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveAccessMatrix(rows);
      toast.success('Access matrix saved');
    } catch (e) {
      toast.error('Failed to save access matrix');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="access-control-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-[#F5A623]/15 flex items-center justify-center">
            <ShieldCheck className="h-6 w-6 text-[#F5A623]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Access Control</h1>
            <p className="text-sm text-muted-foreground">Control which sidebar modules are visible for each Employee Category.</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving || loading} data-testid="save-matrix-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      <Card className="p-4">
        <div className="flex items-end gap-2 max-w-md">
          <div className="flex-1 space-y-1">
            <label className="text-sm font-medium">Add Employee Category</label>
            <Input
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCategory()}
              placeholder="e.g., Plant Operations"
              data-testid="new-category-input"
            />
          </div>
          <Button variant="outline" onClick={addCategory} data-testid="add-category-btn">
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Categories also appear automatically once you assign them to users on the Employee Master page.
        </p>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="no-categories">
          No Employee Categories yet. Add one above or assign categories to users on the Employee Master page.
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="access-matrix-table">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="text-left p-3 font-semibold sticky left-0 bg-muted/40 min-w-[180px]">Employee Category</th>
                {modules.map(m => (
                  <th key={m.key} className="p-3 font-semibold text-center whitespace-nowrap">{m.label}</th>
                ))}
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={row.category} className="border-b hover:bg-accent/40" data-testid={`matrix-row-${row.category}`}>
                  <td className="p-3 font-medium sticky left-0 bg-background">{row.category}</td>
                  {modules.map(m => (
                    <td key={m.key} className="p-3 text-center">
                      <Switch
                        checked={!!row.modules[m.key]}
                        onCheckedChange={() => toggle(idx, m.key)}
                        data-testid={`toggle-${row.category}-${m.key}`}
                      />
                    </td>
                  ))}
                  <td className="p-3 text-center">
                    <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive"
                      onClick={() => removeCategory(row.category)} data-testid={`remove-${row.category}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

export default AccessControlPage;
